import { useEffect, useMemo, useState } from 'react';
import { FileDown } from 'lucide-react';
import { widgetTitle, type Dashboard } from '@kaba/shared';
import { recentBoardIds } from '../../lib/boardNav';
import { Button, Sheet } from '../ui/ui';
import { ReportStatus, TemplateBar, useReportJob, useReportTemplates } from './ReportJob';

/** „Bericht exportieren …“ einer Übersicht: Bausteine wählen, Layout für Papier neu gesetzt */
export function DashboardReportDialog({ dash, open, onClose }: { dash: Dashboard; open: boolean; onClose: () => void }) {
  const all = useMemo(() => dash.widgets.map((w) => w.id), [dash.widgets]);
  const [chosen, setChosen] = useState<string[]>(all);
  const [fullLists, setFullLists] = useState(false);
  const [compact, setCompact] = useState(false);
  const [branding, setBranding] = useState(true);
  const r = useReportJob();
  const tpl = useReportTemplates('dashboard', dash.id, open);
  useEffect(() => {
    if (open) {
      r.reset();
      setChosen(all);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const toggle = (id: string, on: boolean) => setChosen(on ? all.filter((x) => x === id || chosen.includes(x)) : chosen.filter((x) => x !== id));
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()} title="Übersicht als PDF" side="center">
      <div className="flex flex-col gap-4 p-4">
        <p className="text-sm text-[var(--ink-soft)]">
          Die gewählten Bausteine erscheinen in dieser Reihenfolge, für Papier neu gesetzt – mit Datenstand, Zeitraum und Filtern. Diagramme bekommen Legende und Datentabelle.
        </p>
        <fieldset className="flex flex-col gap-1">
          <legend className="mb-1 text-sm font-semibold">Bausteine</legend>
          {dash.widgets.map((w) => (
            <label key={w.id} className="flex min-h-11 items-center gap-2.5 text-sm sm:min-h-9">
              <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={chosen.includes(w.id)} onChange={(e) => toggle(w.id, e.target.checked)} />
              {widgetTitle(w)}
            </label>
          ))}
        </fieldset>
        <fieldset className="flex flex-col gap-1">
          <legend className="mb-1 text-sm font-semibold">Optionen</legend>
          <label className="flex min-h-11 items-center gap-2.5 text-sm sm:min-h-9">
            <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={fullLists} onChange={(e) => setFullLists(e.target.checked)} />
            Listen vollständig (bis 200 Einträge je Baustein)
          </label>
          <label className="flex min-h-11 items-center gap-2.5 text-sm sm:min-h-9">
            <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={compact} onChange={(e) => setCompact(e.target.checked)} />
            Kompakt (weniger Abstände, gleiche Schriftgröße)
          </label>
          {dash.scope === 'board' && (
            <label className="flex min-h-11 items-center gap-2.5 text-sm sm:min-h-9">
              <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={branding} onChange={(e) => setBranding(e.target.checked)} />
              Logo und Farbe des Boards verwenden
            </label>
          )}
        </fieldset>
        <TemplateBar
          t={tpl}
          current={() => ({ widgetIds: chosen, fullLists, branding, compact })}
          onApply={(c) => {
            const cfg = c as { widgetIds: string[]; fullLists: boolean; branding: boolean };
            // Nicht mehr vorhandene Bausteine fallen weg
            setChosen(all.filter((id) => cfg.widgetIds.includes(id)));
            setFullLists(cfg.fullLists);
            setBranding(cfg.branding);
            setCompact(!!(c as { compact?: boolean }).compact);
          }}
        />
        <p className="text-xs text-[var(--ink-soft)]">
          Die Zahlen stammen aus den Boards, auf die du Zugriff hast. Die PDF ist eine eigenständige Datei – ein späterer Entzug des Zugangs holt sie nicht zurück.
        </p>
        <ReportStatus r={r} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{r.job ? 'Schließen' : 'Abbrechen'}</Button>
          <Button
            variant="primary"
            disabled={!chosen.length || r.starting || r.active}
            onClick={() => void r.start({ kind: 'dashboard', dashboardId: dash.id, widgetIds: chosen, fullLists, branding, compact, recentBoardIds: recentBoardIds() })}
          >
            <FileDown size={16} aria-hidden /> {r.job?.status === 'done' ? 'Neu erstellen' : 'PDF erstellen'}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
